import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {RegistrationService} from '@app/features/registration/services/registration.service';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {AccountTypes} from '@app/shared/enums/accountTypes.enum';
import {CaseContact} from '@app/shared/interfaces/caseContact.interface';
import {Clinic} from '@app/shared/interfaces/clinic.interface';
import {Provider} from '@app/shared/interfaces/provider.interface';
import {AccountTypeNamePipe} from '@app/shared/pipes/accountTypeName.pipe';
import {PhoneFormatPipe} from '@app/shared/pipes/phoneFormat.pipe';
import {getAccountType} from '@app/shared/services/helpers';
import {debounceTime, Observable, of, Subscription, switchMap, take} from 'rxjs';
import {
  SecondaryActionButtonComponent
} from '@app/shared/components/secondary-action-button/secondary-action.component';
import {
  PrimaryActionButtonComponent
} from '@app/shared/components/primary-action-button/primary-action-button.component';
import {ColoredPillComponent} from '@app/shared/components/colored-pill/colored-pill.component';
import {LoadSpinnerWrapperComponent} from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import {AdminService} from '@app/features/adminDashboard/services/admin.service';
import {WebResponse} from '@app/shared/interfaces/webResponse.interface';
import {ToastService} from '@app/core/services/toast.service';
import {ConfirmDialogComponent} from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelect, MatSelectModule} from '@angular/material/select';
import {UserAvailability} from '@app/features/registration/interfaces/userAvailability.interface';
import {WorkCompUserInfoUpdateRequest} from '../../interfaces/workCompUserInfoUpdateRequest.interface';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ClinicProviderInfoSectionComponent
} from '../shared/clinic-provider-info-section/clinic-provider-info-section.component';
import {Store} from '@ngrx/store';
import {AppState} from '@app/core/store/app.state';
import {CaseContactSearchTermChanged, FetchCaseContacts} from '../../store/admin.actions';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {NgxMatSelectSearchModule} from 'ngx-mat-select-search';
import {selectCaseContacts} from '../../store/admin.selectors';
import {STEPPER_GLOBAL_OPTIONS} from "@angular/cdk/stepper";
import {
  ReferralUserInfoUpdateRequest
} from "@app/features/adminDashboard/interfaces/referralUserInfoUpdateRequest.interface";
import {UserDetailsService} from "@app/shared/services/userDetails.service";
import {DateFormatPipe} from "@app/shared/pipes/dateFormat.pipe";
import {PortalRequestType} from "@app/features/adminDashboard/interfaces/portalRequest.interface";
import {User} from "@app/core/interfaces";
import {ReferralUser, WorkCompUser} from "@app/core/interfaces/user.interface";
import {
  ClinicProviderInfoPropsProvider,
  ClinicProviderInfoSectionProps
} from "@app/features/adminDashboard/interfaces/clinicProviderInfoSectionProps.interface";
import {us_states} from "@app/shared/interfaces/us_states";

@Component({
  selector: 'app-portal-request-user-info-modal',
  imports: [
    MatDialogModule,
    MatIconModule,
    AccountTypeNamePipe,
    PhoneFormatPipe,
    CommonDirectives,
    SecondaryActionButtonComponent,
    PrimaryActionButtonComponent,
    ColoredPillComponent,
    LoadSpinnerWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    ClinicProviderInfoSectionComponent,
    NgxMaskDirective,
    DateFormatPipe
  ],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {displayDefaultIndicatorType: false},
    },
    provideNgxMask()
  ],
  templateUrl: './portal-request-user-info-modal.component.html',
  styleUrl: './portal-request-user-info-modal.component.scss'
})
// TODO(bradley): This should maybe be split into two components, one for work comp and one for portal, then we can have
// typing on the forms and less confusing code-pathes.
export class PortalRequestUserInfoModalComponent implements OnInit {
  @ViewChild('caseContactSelect') caseContactSelect!: MatSelect;
  @Input() userDetails!: User|ReferralUser|WorkCompUser;
  @Input() userAccountTypeId!: number;
  @Input() requestType!: PortalRequestType;
  @Output() onUserApproved = new EventEmitter<void>();
  @Output() onUserDenied = new EventEmitter<void>();

  protected readonly us_states_list = us_states;

  // TODO: Remove providersList and clinicsList after NextGen API integration
  // Clinics and Providers info will be included in UserDetails response after NextGen API integration
  clinicsList: Clinic[] = [];
  providersList: Provider[] = [];
  caseContactsList: CaseContact[] = [];
  caseContactSearchControl = new FormControl<string>('', {nonNullable: true});
  noCaseContactsFoundText = 'No contacts found';
  selectedCaseContactId: string | null = null;
  selectedCaseContact: CaseContact | null = null;
  initialCaseContactId: string | null = null;

  firstClinicProps: ClinicProviderInfoSectionProps | null = null;
  secondClinicProps: ClinicProviderInfoSectionProps | null = null;

  userAccountType!: AccountTypes;
  AllAccountTypes = AccountTypes;
  emailVerifiedControl = new FormControl<boolean>(false, {nonNullable: true, validators: Validators.requiredTrue});
  emailStatusBgColor = 'bg-light-gray';
  emailStatusTextColor = 'text-dark-gray';
  emailStatusBorderColor = 'border-custom-gray';
  emailStatusText = 'Unverified';
  readOnlyMode = true;
  loading = false;
  secondProviderError = false;

  // Needed so the new requests can show the verification checkbox but
  // existing requests don't.
  hideEmailVerification = false;

  cpInfoForm!: FormGroup;
  firstClinicCtrl!: FormControl;
  firstProvidersCtrl!: FormControl;
  secondClinicCtrl!: FormControl;
  secondProvidersCtrl!: FormControl;
  subscriptions: Subscription = new Subscription();
  referralValues!: FormGroup;
  workCompFormGroup!: FormGroup;

  personalInfoForm!: FormGroup;
  companyInfoFormGroup!: FormGroup;

  userId!: string | null;

  constructor(
    private registrationService: RegistrationService,
    private adminService: AdminService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<PortalRequestUserInfoModalComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private userDetailsService: UserDetailsService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.userId = params?.get('userId');
    });
    this.subscribeToCloseEventSubscriptions();
    this.userAccountType = getAccountType(this.userAccountTypeId);
    // Don't need verification if not new user
    this.hideEmailVerification = (this.requestType != null && this.requestType != PortalRequestType.NewUser);
    this.setVerifiedEmailOnInit(this.hideEmailVerification);
    this.subscriptions.add(
      this.emailVerifiedControl.valueChanges.subscribe((emailVerified: boolean) => {
        this.updateEmailVerificationStyles(emailVerified);
      })
    );
    this.subscriptions.add(
      this.caseContactSearchControl.valueChanges.pipe(debounceTime(1200)).subscribe(() => {
        if(this.caseContactSelect.panelOpen) {
          this.filterCaseContacts();
        }
      })
    );
    this.subscribeToCaseContacts();
    if (this.isWorkCompAccountType()) {
      this.setupWorkCompForm();
      this.patchWorkCompFormValues();
      this.preSelectCaseContact();
    }
    // TODO: Remove following if block after NextGen API integration
    // Clinics and Providers info will be included in UserDetails response after NextGen API integration
    if (true) {
      this.registrationService.getClinicList()
        .pipe(switchMap(clinics => {
          this.clinicsList = clinics;
          return this.registrationService.getProviderList();
        }))
        .subscribe(providers => {
          this.providersList = providers;
          this.syncUserDetailsWithClinicProviderNames();
        });
    }
  }
  getProviderName(providerId: string): ClinicProviderInfoPropsProvider {
    const provider = this.providersList.find(p => p.id == providerId);
    var name = provider ? `${provider.firstName} ${provider.lastName}` : '';
    if(provider?.degree) name += ", " + provider?.degree;
    return {
      firstName: provider?.firstName ?? '',
      lastName: provider?.lastName ?? '',
      degree: provider?.degree ?? '',
      id: providerId,
      name: name
    }
  }

  private getSelectedCaseContactId(): string | null {
    const caseContactId = 'caseContactId' in this.userDetails ? this.userDetails?.caseContactId : null;

    if (caseContactId === null || caseContactId === undefined || caseContactId === '') {
      return null;
    }

    return caseContactId;
  }

  // private getCaseContactFromUserDetails(): CaseContact | null {
  //   const caseContact = this.userDetails?.caseContact ?? this.userDetails?.nextGenCaseContact;
  //   return caseContact && typeof caseContact === 'object'
  //     ? caseContact as CaseContact
  //     : null;
  // }

  onCaseContactSelectionChange(caseContactId: string | null): void {
    this.selectedCaseContactId = caseContactId;
    if (!caseContactId) {
      this.selectedCaseContact = null;
      return;
    }

    this.selectedCaseContact = this.caseContactsList.find(caseContact =>
      String(caseContact.caseContactId || caseContact.id) === String(caseContactId)) || null;
  }

  private isCaseContactRequiredForApproval(): boolean {
    return this.isWorkCompAccountType();
  }

  private hasValidCaseContactSelection(): boolean {
    if (!this.selectedCaseContactId) {
      return false;
    }

    if(!this.workCompFormGroup.valid) {
      return false;
    }

    return this.caseContactsList.some(caseContact =>
      String(caseContact.caseContactId || caseContact.id) === String(this.selectedCaseContactId));
  }

  private IsSecondProviderValid(): boolean {
    if(this.userAccountType !== AccountTypes.REFERRAL_PARTNER) {
      return true;
    }

    if(this.cpInfoForm.get('secondClinicId')?.value != null){
      return this.cpInfoForm.get('secondClinicProviderIds')?.value?.length > 0;
    }
    return (this.cpInfoForm.get('secondClinicProviderIds')?.value?.length ?? 0) == 0;
  }



  private hasCaseContactSelectionChanged(): boolean {
    if(!this.isWorkCompAccountType())
    {
      return false;
    }

    return this.toWorkCompUser(this.userDetails).caseContactId !== this.selectedCaseContactId;
  }

  private parseClinicId(clinicId: unknown): string | null {
    if (clinicId === null || clinicId === undefined || clinicId === '') {
      return null;
    }

    const parsedId = String(clinicId).trim();
    return parsedId.length > 0 ? parsedId : null;
  }

  onEditUser() {
    this.switchToEditMode();
    this.setupFormGroups();
    this.patchFormValues();
  }

  setupFormGroups() {
    let personalInfoFormGroupProps = {};
    let companyInfoFormGroupProps = {};
    let workCompFormGroupProps = {};

    if (this.userAccountType === AccountTypes.REFERRAL_PARTNER) {
      this.cpInfoForm = this.fb.group({
        firstClinicId: [null, Validators.required],
        firstClinicProviderIds: [null, Validators.required],
        secondClinicId: [null],
        secondClinicProviderIds: [null],
        firstClinicOtherProviderName: [null as unknown as string],
        firstClinicOtherProviderNPI: [null as unknown as string],
        secondClinicOtherProviderName: [null as unknown as string],
        secondClinicOtherProviderNPI: [null as unknown as string],
        firstClinicRemovalRequests: [null],
        secondClinicRemovalRequests: [null],
      });

      this.referralValues = this.fb.group({
        jobTitle: [null, Validators.required],
      });

      this.firstClinicCtrl = this.cpInfoForm.controls['firstClinicId'] as FormControl;
      this.firstProvidersCtrl = this.cpInfoForm.controls['firstClinicProviderIds'] as FormControl;
      this.secondClinicCtrl = this.cpInfoForm.controls['secondClinicId'] as FormControl;
      this.secondProvidersCtrl = this.cpInfoForm.controls['secondClinicProviderIds'] as FormControl;

      if((this.userDetails as ReferralUser).secondClinicId) {
        // If a second clinic is already selected, require second clinic providers
        this.secondProvidersCtrl.addValidators([Validators.required]);
      } else {
        // Otherwise, track changes to the second clinic and require providers if one is selected.
        this.subscriptions.add(
          this.secondClinicCtrl.valueChanges.subscribe((val) => {
            if(!val) return;

            if(!this.secondProvidersCtrl.hasValidator(Validators.required)) {
              this.secondProvidersCtrl.addValidators([Validators.required]);
            }
          })
        );
      }
    }

    personalInfoFormGroupProps = {
      email: [null as unknown as string, [Validators.required, Validators.email]],
      phone: [null as unknown as string, [Validators.required]],
      faxNumber: [null as unknown as string]
    };

    if(this.isWorkCompAccountType())
    {
      const wcUser = this.toWorkCompUser(this.userDetails);
      personalInfoFormGroupProps = {
        ...personalInfoFormGroupProps,
        address: [null as unknown as string, [Validators.required]],
        city: [null as unknown as string, [Validators.required]],
        state: [null as unknown as string, [Validators.required]],
        zipcode: [null as unknown as string, [Validators.required,Validators.pattern(/^\d{5}$/)]]
      }
    }

    if('managerFirstName' in this.userDetails) {
      companyInfoFormGroupProps = { ...companyInfoFormGroupProps,
        managerFirstName: [null as unknown as string, Validators.required],
        managerLastName: [null as unknown as string, Validators.required],
        managerEmail: [null as unknown as string, [Validators.required, Validators.email]],
        faxForInformation: [null as unknown as string, Validators.required],
        faxForChartNote: [null as unknown as string, Validators.required]
      };
    }

    if('companyName' in this.userDetails) {
      companyInfoFormGroupProps = {
        ...companyInfoFormGroupProps,
        companyName: [null as unknown as string, Validators.required]
      };
    }

    this.personalInfoForm = this.fb.group(personalInfoFormGroupProps);
    this.companyInfoFormGroup = this.fb.group(companyInfoFormGroupProps);
  }

  private setupWorkCompForm() {
    const workCompFormGroupProps = {
      caseContactId: [null as unknown as string, Validators.required]
    };

    this.workCompFormGroup = this.fb.group(workCompFormGroupProps);
  }

  patchFormValues() {
    let personalInfoFormPatchValues = {};

    personalInfoFormPatchValues = {
      email: this.userDetails.email,
      phone: this.userDetails.phoneNumber,
      faxNumber: this.userDetails.faxNumber
    };

    if(this.isWorkCompAccountType()) {
      personalInfoFormPatchValues = {
        ...personalInfoFormPatchValues,
        address: this.userDetails.address,
        city: this.userDetails.city,
        state: this.userDetails.state,
        zipcode: this.userDetails.postalCode
      }
    }

    this.personalInfoForm.patchValue(personalInfoFormPatchValues);
    this.personalInfoForm.markAsPristine();
    this.personalInfoForm.markAsUntouched();

    if (this.userAccountType === AccountTypes.ADMIN) {
      return;
    }

    this.initialCaseContactId = this.selectedCaseContactId;

    let companyInfoFormPatchValues = {};
    let referralFormPatchValues = {};
    let cpInfoFormPatchValues = {};

    if('managerFirstName' in this.userDetails) {
      companyInfoFormPatchValues = {
        ...companyInfoFormPatchValues,
        managerFirstName: this.userDetails?.managerFirstName,
        managerLastName: this.userDetails?.managerLastName,
        managerEmail: this.userDetails?.managerEmail,
        faxForInformation: this.userDetails?.faxForInformation,
        faxForChartNote: this.userDetails?.faxForChartNote
      };
    }

    if('companyName' in this.userDetails) {
      companyInfoFormPatchValues = {
        ...companyInfoFormPatchValues,
        companyName: this.userDetails?.companyName
      };
    }

    if (this.userAccountType === AccountTypes.REFERRAL_PARTNER) {
      const referralUser = this.userDetails as ReferralUser;

      referralFormPatchValues = {
        jobTitle: referralUser?.jobTitle
      };
      this.referralValues.patchValue(referralFormPatchValues);
      this.referralValues.markAsPristine();
      this.referralValues.markAsUntouched();

      const firstClinicId = this.parseClinicId(referralUser?.firstClinicId);
      const secondClinicId = this.parseClinicId(referralUser?.secondClinicId);

      cpInfoFormPatchValues = {
        ...cpInfoFormPatchValues,
        firstClinicId,
        firstClinicProviderIds: (referralUser.firstClinicProviderIds ?? []),
        secondClinicId,
        secondClinicProviderIds: (referralUser.secondClinicProviderIds ?? []),
        firstClinicOtherProviderName: referralUser?.firstClinicOtherProviderName,
        firstClinicOtherProviderNPI: referralUser?.firstClinicOtherProviderNPI,
        secondClinicOtherProviderName: referralUser.secondClinicOtherProviderName,
        secondClinicOtherProviderNPI: referralUser.secondClinicOtherProviderNPI,
        firstClinicRemovalRequests: referralUser.requestedRemovalFirstClinicProviderIds,
        secondClinicRemovalRequests: referralUser.requestedRemovalSecondClinicProviderIds
      };
    }

    if(this.companyInfoFormGroup && companyInfoFormPatchValues) {
      this.companyInfoFormGroup.patchValue(companyInfoFormPatchValues);
      this.companyInfoFormGroup.markAsPristine();
      this.companyInfoFormGroup.markAsUntouched();
    }

    if(this.cpInfoForm && cpInfoFormPatchValues) {
      this.cpInfoForm.patchValue(cpInfoFormPatchValues);
      this.cpInfoForm.markAsPristine();
      this.cpInfoForm.markAsUntouched();
    }
  }

  private patchWorkCompFormValues() {
    const wcUser = this.toWorkCompUser(this.userDetails);
    const wcFormPatchValues = {
      caseContactId: wcUser.caseContactId === "00000000-0000-0000-0000-000000000000" ? null : wcUser.caseContactId,
    }
    this.workCompFormGroup.patchValue(wcFormPatchValues);
    this.workCompFormGroup.markAsPristine();
  }

  onApproveUser() {
    this.emailVerifiedControl.markAsTouched();

    if (this.isCaseContactRequiredForApproval()) {
      this.workCompFormGroup.markAllAsTouched();

      if(!this.hasValidCaseContactSelection()) {
        return;
      }
    }
    if (this.emailVerifiedControl.invalid) {
      return;
    }
    this.loading = true;

    const approvalInfo: WorkCompUserInfoUpdateRequest = {
      email: this.userDetails?.email ?? '',
      ...(this.selectedCaseContactId
        ? { caseContact: { caseContactId: this.selectedCaseContactId } }
        : {})
    };

    this.adminService.approvePortalRequest$(this.userDetails.subId, approvalInfo)
      .subscribe((response: WebResponse<boolean>) => {
        this.loading = false;
        if (response.succeeded) {
          this.onUserApproved.emit();
          this.toastService.showSuccessToast('User approved successfully.');
          this.closeDialog();
        } else {
          this.toastService.showErrorToast('Unable to approve user. Please try again.');
        }
      });
  }

  onDenyUser() {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Deny Request';
    dialog.componentInstance.content = this.denyRequestMessage;
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => {
        this.initiateDenyProcess();
      });
  }

  // Existing requests don't require manual email verification, so initialize this control as verified.
  setVerifiedEmailOnInit(emailVerified: boolean) {
    this.emailVerifiedControl.setValue(emailVerified, {emitEvent: false});
    this.emailVerifiedControl.markAsUntouched();
    this.updateEmailVerificationStyles(emailVerified);
  }

  private updateEmailVerificationStyles(emailVerified: boolean): void {
    this.emailStatusBgColor = emailVerified ? 'bg-light-green' : 'bg-light-gray';
    this.emailStatusTextColor = emailVerified ? 'text-dark-green' : 'text-dark-gray';
    this.emailStatusBorderColor = emailVerified ? 'border-green' : 'border-custom-gray';
    this.emailStatusText = emailVerified ? 'Verified' : 'Unverified';
  }

  initiateDenyProcess() {
    this.loading = true;
    this.adminService.denyPortalRequest$(this.userDetails.subId)
      .subscribe((response: WebResponse<boolean>) => {
        this.loading = false;
        if (response.succeeded) {
          this.onUserDenied.emit();
          this.toastService.showSuccessToast('User denied successfully.');
          this.closeDialog();
        } else {
          this.toastService.showErrorToast('Unable to remove user. Please try again.');
        }
      });
  }

  onCancel() {
    if (this.anyUnsavedChanges) {
      this.showUnsavedChangesConfirmDialog(() => this.switchToReadOnlyMode());
    } else {
      this.switchToReadOnlyMode();
    }
  }

  onSave() {
    if (this.isCaseContactRequiredForSave() && !this.hasValidCaseContactSelection()) {
      return;
    }
    if (this.userAccountType === AccountTypes.REFERRAL_PARTNER && !this.IsSecondProviderValid()) {
      this.secondProviderError = true;
      return;
    }
    this.secondProviderError = false;
    if (!this.shouldSaveInitiate()) { return; }
    this.saveUpdatedInfo();
  }

  onFirstClinicOtherProviderRemoved() {
    if(!this.cpInfoForm) return;

    this.cpInfoForm.patchValue(
      {
        firstClinicOtherProviderName: null,
        firstClinicOtherProviderNPI: null
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onSecondClinicOtherProviderRemoved() {
    this.cpInfoForm.patchValue(
      {
        secondClinicOtherProviderName: null,
        secondClinicOtherProviderNPI: null
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onFirstClinicRemovalRequestRemoved() {
    if(!this.cpInfoForm) return;

    this.cpInfoForm.patchValue(
      {
        firstClinicRemovalRequests: [],
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onSecondClinicRemovalRequestRemoved() {
    this.cpInfoForm.patchValue(
      {
        secondClinicRemovalRequests: [],
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  private isWorkCompAccountType(): boolean {
    return this.userAccountType === AccountTypes.EMPLOYER_OF_WORK_COMP_PATIENT
      || this.userAccountType === AccountTypes.CASE_MANAGER
      || this.userAccountType === AccountTypes.INSURANCE_ADJUSTER;
  }

  private isCaseContactRequiredForSave(): boolean {
    return this.isWorkCompAccountType();
  }

  handleClose() {
    if (this.anyUnsavedChanges) {
      this.showUnsavedChangesConfirmDialog(() => {
        this.closeDialog();
      });
    } else {
      this.closeDialog();
    }
  }

  private closeDialog() {
    this.subscriptions.unsubscribe();
    this.dialogRef.close();
  }

  private saveUpdatedInfo() {
    this.loading = true;
    const email: string = this.personalInfoForm.value.email ?? '';
    if(email !== this.userDetails.email) {
      this.registrationService.checkEmailAvailability(email)
        .pipe(switchMap((response: UserAvailability) => {
          if (response.emailTaken) {
            this.loading = false;
            this.setPersonalInfoErrors();
            return of(false);
          } else {
            this.removePersonalInfoErrors();
            return of(true);
          }
        }))
        .subscribe((emailAvailable: boolean) => {
          if (emailAvailable) {
            this.saveInfo(email);
          }
        });
    } else {
      this.saveInfo(email);
    }
  }

  private saveInfo(email: string){
    let userUpdate$: Observable<WebResponse<boolean>> | null = null;

    if (this.userAccountType !== AccountTypes.REFERRAL_PARTNER) {
      const updatedInfo: WorkCompUserInfoUpdateRequest = this.getUserValuesFromForm(email);
      userUpdate$ = this.adminService.updateWorkCompUserInfoForPortalRequest(this.userDetails.subId, updatedInfo);
    } else {
      const model = this.getUserValuesFromForm(email);
      userUpdate$ = this.adminService.updateReferralUserInfoForPortalRequest(this.userDetails.subId, model);
    }

    if(userUpdate$ === null) return;

    userUpdate$.pipe(
      take(1),
      switchMap((response: WebResponse<boolean>)=> {
        this.loading = false;
        if (response.succeeded) {
          this.toastService.showSuccessToast('User information updated successfully.');
          return this.userDetailsService.getUserDetails$(this.userDetails.subId, this.userAccountType).pipe(take(1));
        } else {
          this.toastService.showErrorToast('Unable to update user information. Please try again.');
          return of(null);
        }
      })
    ).subscribe(newUserDetails => {
      if(newUserDetails) {
        this.userDetails = newUserDetails;
        // Unset these, they're set in the sync function and keeping them set just leads to possible stale values
        this.firstClinicProps = null;
        this.secondClinicProps = null;
        this.syncUserDetailsWithClinicProviderNames();
        this.patchFormValues();
        this.switchToReadOnlyMode();
      }
    })
  }

  private updateUserDetailsAfterSuccessfulUpdate(email: string): void {
    const updatedUserDetails = { ...this.userDetails, email };
    if (this.isWorkCompAccountType()) {
      // updatedUserDetails.caseContactId = this.selectedCaseContactId;
      // if (this.selectedCaseContact) {
      //   updatedUserDetails.caseContact = this.selectedCaseContact;
      //   updatedUserDetails.nextGenCaseContact = this.selectedCaseContact;
      // }
    }
    this.userDetails = updatedUserDetails;
    this.initialCaseContactId = this.selectedCaseContactId;
  }

  private getUserValuesFromForm(email: string): ReferralUser | WorkCompUser | User {
    const user: User = {
      id: this.userDetails.id,
      subId: this.userDetails.subId,
      userStatusLookupId: this.userDetails.userStatusLookupId,
      address: this.userDetails.address,
      faxNumber: this.userDetails.faxNumber,
      state: this.userDetails.state,
      firstName: this.userDetails.firstName,
      lastName: this.userDetails.lastName,
      postalCode: this.userDetails.postalCode,
      city: this.userDetails.city,
      phoneNumber: this.personalInfoForm.get('phone')?.value ?? this.userDetails.phoneNumber,
      email: email
    };

    switch(this.userAccountType) {
      case AccountTypes.CASE_MANAGER:
      case AccountTypes.INSURANCE_ADJUSTER:
      case AccountTypes.EMPLOYER_OF_WORK_COMP_PATIENT:
        return {
          ...user,
          address: this.personalInfoForm.get('address')?.value ?? this.userDetails.address,
          faxNumber: this.personalInfoForm.get('faxNumber')?.value ?? this.userDetails.faxNumber,
          state: this.personalInfoForm.get('state')?.value ?? this.userDetails.state,
          firstName: this.personalInfoForm.get('firstName')?.value ?? this.userDetails.firstName,
          lastName: this.personalInfoForm.get('lastName')?.value ?? this.userDetails.lastName,
          postalCode: this.personalInfoForm.get('zipcode')?.value ?? this.userDetails.postalCode,
          city: this.personalInfoForm.get('city')?.value ?? this.userDetails.city,
          caseContactId: this.selectedCaseContactId,
          companyName: this.companyInfoFormGroup?.get('companyName')?.value ?? '',
          managerEmail: this.companyInfoFormGroup?.get('managerEmail')?.value ?? '',
          managerFirstName: this.companyInfoFormGroup?.get('managerFirstName')?.value ?? '',
          managerLastName: this.companyInfoFormGroup?.get('managerLastName')?.value ?? '',
          faxForInformation: this.companyInfoFormGroup?.get('faxForInformation')?.value ?? '',
          faxForChartNote: this.companyInfoFormGroup?.get('faxForChartNote')?.value ?? '',
        } as WorkCompUser;
      case AccountTypes.REFERRAL_PARTNER:
        return {
          ...user,
          jobTitle: this.referralValues.get('jobTitle')?.value,
          firstClinicProviderIds: this.cpInfoForm.get('firstClinicProviderIds')?.value,
          secondClinicProviderIds: this.cpInfoForm.get('secondClinicProviderIds')?.value,
          firstClinicId: this.cpInfoForm.get('firstClinicId')?.value,
          secondClinicId: this.cpInfoForm.get('secondClinicId')?.value,
          firstClinicOtherProviderName: this.cpInfoForm.get('firstClinicOtherProviderName')?.value,
          firstClinicOtherProviderNPI: this.cpInfoForm.get('firstClinicOtherProviderNPI')?.value,
          secondClinicOtherProviderName: this.cpInfoForm.get('secondClinicOtherProviderName')?.value,
          secondClinicOtherProviderNPI: this.cpInfoForm.get('secondClinicOtherProviderNPI')?.value,
          requestedRemovalFirstClinicProviderIds: this.cpInfoForm.get('firstClinicRemovalRequests')?.value,
          requestedRemovalSecondClinicProviderIds: this.cpInfoForm.get('secondClinicRemovalRequests')?.value,
          // If there is no second clinic, then we use what is set for second clinic, which will be null
          // or set to what we want to remove.
          requestedRemovalSecondClinicId: (!!this.cpInfoForm.get('secondClinicId')?.value) ? null :
            this.toReferralUser(this.userDetails).secondClinicId,
          managerEmail: this.companyInfoFormGroup.get('managerEmail')?.value ?? '',
          managerFirstName: this.companyInfoFormGroup.get('managerFirstName')?.value ?? '',
          managerLastName: this.companyInfoFormGroup.get('managerLastName')?.value ?? '',
          faxForInformation: this.companyInfoFormGroup?.get('faxForInformation')?.value ?? '',
          faxForChartNote: this.companyInfoFormGroup?.get('faxForChartNote')?.value ?? '',
        } as ReferralUser
    }

    return user;
  }

  private setPersonalInfoErrors() {
    this.personalInfoForm.get('email')?.setErrors({ emailTaken: true });
  }

  private removePersonalInfoErrors() {
    this.personalInfoForm.get('email')?.setErrors(null);
  }

  private shouldSaveInitiate(): boolean {
    return this.anyUnsavedChanges &&
      (this.personalInfoForm?.valid ?? true) &&
      (this.referralValues?.valid ?? true) &&
      (this.companyInfoFormGroup?.valid ?? true) &&
      (this.cpInfoForm?.valid ?? true);
  }

  get anyUnsavedChanges(): boolean {
    if (this.isReadOnlyMode()) { return false; }

    return this.hasCaseContactSelectionChanged() ||
      this.personalInfoForm?.dirty ||
      this.referralValues?.dirty ||
      this.companyInfoFormGroup?.dirty ||
      this.cpInfoForm?.dirty;
  }

  private switchToReadOnlyMode() {
    this.readOnlyMode = true;
    this.secondProviderError = false;
  }

  private switchToEditMode() {
    this.readOnlyMode = false;
  }

  private isReadOnlyMode(): boolean {
    return this.readOnlyMode;
  }

  private subscribeToCloseEventSubscriptions() {
    this.subscriptions.add(
      this.dialogRef.backdropClick().subscribe((event) => {
        event.preventDefault();
        this.handleClose();
      })
    );

    this.subscriptions.add(
      this.dialogRef.keydownEvents().subscribe(event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.handleClose();
        }
      })
    );
  }

  private subscribeToCaseContacts() {
    this.subscriptions.add(
      this.store.select(selectCaseContacts)
        .subscribe((caseContacts: CaseContact[]) => {
          this.caseContactsList = caseContacts;
        })
    );
  }

  private filterCaseContacts(): void {
    const search = this.caseContactSearchControl.value.trim().toLowerCase();
    this.fetchFilteredCaseContacts(search);
  }

  private preSelectCaseContact(): void {
    const caseContactId = this.selectedCaseContactId ?? this.getSelectedCaseContactId();

    if(caseContactId)
    {
      this.selectedCaseContactId = caseContactId;
      this.subscriptions.add(
        this.registrationService.getCaseContact$(caseContactId)
          .subscribe((caseContact) => {
            if(caseContact) {
              this.caseContactsList = [caseContact];
            }
            this.selectedCaseContact = caseContact;
          })
      );
    } else {
      this.fetchFilteredCaseContacts(this.userDetails.email);
    }
  }

  private fetchFilteredCaseContacts(searchTerm: string): void {
    this.store.dispatch(CaseContactSearchTermChanged({ searchTerm }));
  }

  private showUnsavedChangesConfirmDialog(onConfirm: () => void) {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => onConfirm());
  }

  private syncUserDetailsWithClinicProviderNames(): void {
    const userDetails = ('firstClinicId' in this.userDetails ? this.userDetails : null) as ReferralUser;

    if(!userDetails) return;

    const firstClinicProps = { ...this.firstClinicProps };
    const secondClinicProps = { ...this.secondClinicProps };

    if (userDetails.firstClinicId) {
      firstClinicProps.clinicId = userDetails.firstClinicId;
    }
    const secondClinicId = userDetails.pendingRemovalSecondClinicId ?? userDetails.secondClinicId;
    if (secondClinicId) {
      secondClinicProps.clinicId = secondClinicId;
    }
    secondClinicProps.pendingRemovalClinicId = userDetails.pendingRemovalSecondClinicId ?? null;
    if (userDetails.firstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.providers =
        userDetails.firstClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }
    if (userDetails.pendingFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.pendingProviders =
        userDetails.pendingFirstClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }
    if (userDetails.pendingRemovalFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.pendingRemovalProviders =
        userDetails.pendingRemovalFirstClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }
    if (userDetails.requestedRemovalFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.requestedRemovalProviders =
        userDetails.requestedRemovalFirstClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    } else {
      firstClinicProps.requestedRemovalProviders = [];
    }

    if (userDetails.secondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.providers =
        userDetails.secondClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }
    if (userDetails.pendingSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.pendingProviders =
        userDetails.pendingSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }
    if (userDetails.pendingRemovalSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.pendingRemovalProviders =
        userDetails.pendingRemovalSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    }

    if (userDetails.requestedRemovalSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.requestedRemovalProviders =
        userDetails.requestedRemovalSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id) });
    } else {
      secondClinicProps.requestedRemovalProviders = [];
    }
    firstClinicProps.otherProviderName = userDetails.firstClinicOtherProviderName;
    firstClinicProps.otherProviderNpi = userDetails.firstClinicOtherProviderNPI;

    secondClinicProps.otherProviderName = userDetails.secondClinicOtherProviderName;
    secondClinicProps.otherProviderNpi = userDetails.secondClinicOtherProviderNPI;

    this.firstClinicProps = firstClinicProps as ClinicProviderInfoSectionProps;
    this.secondClinicProps = secondClinicProps as ClinicProviderInfoSectionProps;
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.anyUnsavedChanges) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }

  get denyRequestMessage(): string {
    return this.requestType != PortalRequestType.NewUser
      ? 'Are you sure you want to deny this user request?'
      : 'Are you sure you want to deny this user access? The user will remain in User Management with a Denied status.';
  }

  toReferralUser(userDetails: User | ReferralUser | WorkCompUser): ReferralUser {
    return userDetails as ReferralUser;
  }

  toWorkCompUser(userDetails: User | ReferralUser | WorkCompUser): WorkCompUser {
    return userDetails as WorkCompUser;
  }
}
